// The rails admin client. Mirrors apps/api/src/routes/rails-admin.ts and
// apps/api/src/rails/admin.ts; nothing here filters, sorts or decides anything the API
// did not, so what is on the screen is what the database allowed.
//
// It keeps its own fetch helpers rather than reaching into api.ts, so the two surfaces
// can be worked on apart; the error classes are shared, because a caller that has been
// signed out should be sent to sign in whichever page it was on.
import { viewFetch as fetch } from './view-as.js';
import { ApiError, Unauthenticated, type Store } from './api.js';
import { withDeadline } from './admin/deadline.js';

export type AccessLevel = 'none' | 'read' | 'write';
export type RouteTo = 'personal' | 'commercial';

export interface GroupView { id: string; code: string; name: string; description: string | null; members: number }

export interface GroupReading {
  groupId: string | null; groupName: string | null; groupTiers: string[]; tiers: string[];
  team: { principalId: string; name: string }[];
}
export interface GroupMovePreview { principalId: string; name: string; before: GroupReading; after: GroupReading }

export interface MemberView {
  principalId: string;
  displayName: string;
  upn: string;
  role: string;
  status: string;
  /** Whether this person has a private store. Whether, never what is in it (R17), nor how many (R32). */
  hasPersonalStore: boolean;
  /** On the caller's own row only: their personal stores by the names they gave them, own first (R32, B200). */
  personalStoreLabels?: string[];
  groupId: string | null;
  groupCode: string | null;
  managerPrincipalId: string | null;
  grants: string[];
  exceptions: Array<{ domainTier: string; access: AccessLevel }>;
}

export interface MatrixCell { groupId: string; domainTier: string; access: AccessLevel }
/** R37: what a group is shared of the recorded calls -- internal ones, all, or nothing beyond the invite (null). */
export interface MeetingShareRuleView { groupId: string; groupCode: string; groupName: string; scope: 'internal' | 'all' | null; setAt: string | null }
export interface Matrix { tiers: string[]; groups: Array<{ id: string; code: string; name: string }>; cells: MatrixCell[] }

export interface WallView {
  id: string;
  orgAId: string; orgAName: string;
  orgBId: string; orgBName: string;
  active: boolean;
  notes: string | null;
  sourceSignalId: string | null;
  setBy: string | null;
  createdAt: string;
}

export interface OrganisationView { id: string; name: string; counterpartyClass: string }

/** A version of the staff-facing monitoring policy (R22, R24). The words are the company's. */
export interface PolicyView {
  id: string;
  versionNo: number;
  body: string;
  publishedAt: string | null;
  publishedBy: string | null;
  createdAt: string;
  acks: number;
  ackedByMe: boolean;
}

/** One person's overload setting (R10). Never the caller's own: the policy hides that row. */
export interface AffectView {
  subjectPrincipalId: string;
  subjectName: string;
  enabled: boolean;
  reportToPrincipalId: string | null;
  reportToName: string | null;
  reviewNote: string | null;
  reviewDoneAt: string | null;
  enabledBy: string | null;
}

/** Someone whose view this caller may take (R6). */
export interface ReportView { principalId: string; displayName: string; upn: string }

export interface RouteView {
  id: string;
  matchKind: string;
  matchValue: string | null;
  routeTo: RouteTo;
  ownerPrincipalId: string;
  ownerName: string;
  createdAt: string;
  learnedBy: string | null;
  verdicts: { total: number; personal: number; commercial: number } | null;
  decisions: number;
  retiredAt: string | null;
  retiredBy: string | null;
  retireReason: string | null;
  /** How many store questions the retirement raised to the owner (R30). A count, never the items (R31). */
  questionsRaised: number | null;
}

/** A question put to the owner: a retired route moved this item into their private store; where does it belong? (R30) */
export interface StoreQuestion {
  id: string;
  /** The personal store the question and the item are in. */
  store: string;
  signalId: string;
  subject: string | null;
  occurredAt: string | null;
  kind: string | null;
  fromName: string | null;
  fromAddress: string | null;
  preview: string | null;
  askedAt: string;
}

export type StoreAnswer = 'commercial' | 'personal';

/** One of the caller's own personal stores, by the label they gave it (R32). */
export interface MyPersonalStore { tenantId: string; label: string | null; holdsForAnother: boolean; createdAt: string }

async function json<T>(res: Response): Promise<T> {
  if (res.status === 401) throw new Unauthenticated();
  if (!res.ok) {
    const said = await res.json()
      .then((b: unknown) => (typeof b === 'object' && b !== null && typeof (b as { error?: unknown }).error === 'string' ? (b as { error: string }).error : null))
      .catch(() => null);
    throw new ApiError(res.status, said ?? `${res.status} ${res.statusText}`);
  }
  return (await res.json()) as T;
}

// As on the admin console, every GET carries a deadline (admin/deadline.ts). The Routes tab is
// the read that made this necessary: it never answered on the live store, and the transactions
// it left behind held a lock that failed another session's migration twice.
const get = <T>(path: string, signal?: AbortSignal) =>
  withDeadline(path, { credentials: 'same-origin' }, json<T>, signal);
const send = <T>(method: 'POST' | 'PUT' | 'PATCH', path: string, body: unknown) =>
  fetch(path, { method, credentials: 'same-origin', headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) }).then((r) => json<T>(r));

const q = encodeURIComponent;

export const rails = {
  meetingSharing: (store: string) => get<{ store: Store; rules: MeetingShareRuleView[] }>(`/api/admin/rails/meeting-sharing?store=${q(store)}`),
  setMeetingSharing: (store: string, groupId: string, scope: 'internal' | 'all' | 'none') =>
    send<{ store: Store; rule: MeetingShareRuleView }>('PUT', `/api/admin/rails/meeting-sharing/${q(groupId)}`, { store, scope }),
  groups: (store: string) => get<{ store: Store; groups: GroupView[]; members: MemberView[] }>(`/api/admin/rails/groups?store=${q(store)}`),
  createGroup: (store: string, g: { code: string; name: string; description?: string }) =>
    send<{ store: Store; group: GroupView }>('POST', '/api/admin/rails/groups', { store, ...g }),
  previewGroupMove: (store: string, principalId: string, groupId: string) =>
    get<{ store: Store; preview: GroupMovePreview }>(`/api/admin/rails/groups/members/${q(principalId)}/preview?store=${q(store)}&groupId=${q(groupId)}`),
  setPrimaryGroup: (store: string, principalId: string, groupId: string) =>
    send<{ store: Store; member: { principalId: string; groupId: string } }>('PUT', `/api/admin/rails/groups/members/${q(principalId)}`, { store, groupId }),

  matrix: (store: string) => get<{ store: Store } & Matrix>(`/api/admin/rails/matrix?store=${q(store)}`),
  // Unused by any page (17 Oct 2026); MA6 guards its route with a reason
  setCell: (store: string, cell: MatrixCell) =>
    send<{ store: Store; cell: MatrixCell }>('PUT', '/api/admin/rails/matrix', { store, ...cell }),

  walls: (store: string) => get<{ store: Store; walls: WallView[] }>(`/api/admin/rails/walls?store=${q(store)}`),
  organisations: (store: string, search: string, limit = 20) =>
    get<{ store: Store; organisations: OrganisationView[] }>(`/api/admin/rails/organisations?store=${q(store)}&q=${q(search)}&limit=${limit}`),
  createWall: (store: string, w: { orgAId: string; orgBId: string; notes?: string }) =>
    send<{ store: Store; wall: WallView }>('POST', '/api/admin/rails/walls', { store, ...w }),
  setWallActive: (store: string, id: string, active: boolean, reason: string) =>
    send<{ store: Store; wall: WallView }>('PATCH', `/api/admin/rails/walls/${q(id)}`, { store, active, reason }),

  setGrant: (store: string, principalId: string, domainTier: string, granted: boolean) =>
    send<{ store: Store; changed: boolean }>('PUT', `/api/admin/rails/people/${q(principalId)}/grants`, { store, domainTier, granted }),
  setException: (store: string, principalId: string, domainTier: string, access: AccessLevel | null, reason: string) =>
    send<{ store: Store; changed: boolean }>('PUT', `/api/admin/rails/people/${q(principalId)}/exceptions`, { store, domainTier, access, reason }),
  setManager: (store: string, principalId: string, managerPrincipalId: string | null) =>
    send<{ store: Store; changed: boolean }>('PUT', `/api/admin/rails/people/${q(principalId)}/manager`, { store, managerPrincipalId }),

  createPersonalStore: (store: string, principalId: string) =>
    send<{ store: Store; personalTenantId: string }>('POST', `/api/admin/rails/people/${q(principalId)}/personal-store`, { store }),

  affect: (store: string) => get<{ store: Store; settings: AffectView[] }>(`/api/admin/rails/affect?store=${q(store)}`),
  setAffect: (store: string, principalId: string, body: { enabled: false } | { enabled: true; reportToPrincipalId: string; reviewNote: string }) =>
    send<{ store: Store; changed: boolean }>('PUT', `/api/admin/rails/affect/${q(principalId)}`, { store, ...body }),

  policies: (store: string) => get<{ store: Store; policies: PolicyView[] }>(`/api/admin/rails/policy?store=${q(store)}`),
  draftPolicy: (store: string, body: string) => send<{ store: Store; policy: PolicyView }>('POST', '/api/admin/rails/policy', { store, body }),
  publishPolicy: (store: string, id: string) => send<{ store: Store; policy: PolicyView }>('POST', `/api/admin/rails/policy/${q(id)}/publish`, { store }),

  routes: (store: string, opts: { search?: string; retired?: boolean } = {}) =>
    get<{ store: Store; includeRetired: boolean; limit: number; total?: number; routes: RouteView[] }>(
      `/api/admin/rails/routes?store=${q(store)}&q=${q(opts.search ?? '')}${opts.retired ? '&retired=1' : ''}`),
  retireRoute: (store: string, id: string, reason: string) =>
    send<{ store: Store; route: RouteView }>('POST', `/api/admin/rails/routes/${q(id)}/retire`, { store, reason }),
  /** R30, R31: how many items this route moved that still sit in its owner's private store. A count, never the items. */
  routeMoves: (store: string, id: string) =>
    get<{ store: Store; routeId: string; moves: number }>(`/api/admin/rails/routes/${q(id)}/moves?store=${q(store)}`),
};

/**
 * The two rails surfaces an ordinary user meets: the published monitoring policy and the
 * people whose view they may take. Not admin routes, so they are not under `rails`.
 */
export const railsMe = {
  policy: (store: string) => get<{ store: Store; policy: PolicyView | null }>(`/api/policy?store=${q(store)}`),
  ackPolicy: (store: string, id: string) => send<{ store: Store; first: boolean; policy: PolicyView | null }>('POST', `/api/policy/${q(id)}/ack`, { store }),
  reports: (store: string) => get<{ store: Store; reports: ReportView[] }>(`/api/reports?store=${q(store)}`),
  /** R30: the owner's questions about which store an item a retired route moved belongs in, across their personal stores. */
  storeQuestions: () => get<{ questions: StoreQuestion[] }>('/api/privacy-flags/store'),
  answerStore: (store: string, id: string, belongs: StoreAnswer) =>
    send<{ store: string; question: { id: string; signalId: string; answeredStore: StoreAnswer; answeredAt: string; moved: boolean } }>(
      'POST', `/api/privacy-flags/store/${q(id)}/answer`, { store, belongs }),
  /** R32: the caller's own personal stores by label, own first. Nobody else's. */
  personalStores: () => get<{ count: number; stores: MyPersonalStore[] }>('/api/me/personal-stores'),
  setStoreLabel: (tenantId: string, label: string) =>
    send<{ store: string; label: string }>('PUT', `/api/me/personal-stores/${q(tenantId)}/label`, { label }),
};

/** The tier table of module 05 DESIGN layer 3, so the matrix reads as words and not as codes. */
export const TIER_LABELS: Record<string, { domain: string; covers: string }> = {
  L1: { domain: 'Legal', covers: 'Client and supplier contracts, NDAs, rights agreements' },
  L2: { domain: 'Legal', covers: 'Corporate: share sales, investment, M&A, board matters' },
  L3: { domain: 'Legal', covers: 'Disputes with or about an employee; regulatory investigations' },
  F1: { domain: 'Finance', covers: 'Client invoicing, supplier bills, receipts' },
  F2: { domain: 'Finance', covers: 'Payroll, compensation, benefits' },
  F3: { domain: 'Finance', covers: 'Corporate finance, investors, fundraising, valuations' },
  H1: { domain: 'HR', covers: 'General people operations: hiring, policies, onboarding' },
  H2: { domain: 'HR', covers: 'Matters concerning a named employee: performance, grievance, exit' },
  C1: { domain: 'Commercial', covers: 'Client and prospect conversations, pipeline, meeting notes' },
  C2: { domain: 'Commercial', covers: 'Pricing, deal terms, margins, rev-share models' },
  M1: { domain: 'Marketing', covers: 'Brand, PR, events, content' },
  O1: { domain: 'Ops', covers: 'Delivery, MCR, incidents, encoders, supplier ops' },
};
